import React, {ReactElement, useEffect, useMemo} from 'react';
import {useWindowSizingProvider} from './windowSizingProvider';
import {useConversationsProvider} from './conversationsProvider';
import {useReactionsProvider} from './reactionsProvider';
import {useSelectedConversationProvider} from './selectedConversationProvider';
import {useSelectedReactionGroupProvider} from './selectedReactionProvider';
import {useCanvasProvider} from './canvasProvider';
import {useCommentsProvider} from './commentsProvider';
import {useAnnotationManagerProvider} from './annotationManagerProvider';
import {useDisableConversationProvider} from './disableConversationProvider';
import {useLibraryProvider} from './libraryProvider';
import {useCollectionsProvider} from './collectionsProvider';
import {useCollectionMediaProvider} from './collectionMediaProvider';
import {useFoldersProvider} from './foldersProvider';
import {useBatchSelectionProvider} from './batchSelectionProvider';
import {useMediaProvider} from './mediaProvider';
import {useCaptionDataProvider} from './captionDataProvider';
import {useSortMetricProvider} from '../service/sortMetricProvider';
import {useUserQuotaStore} from './userQuotaStore';
import {useMigrationProvider} from './migrationProvider';
import {useSelfIdentifiedUserProvider} from './selfIdentifiedUserProvider';
import {useMediaOperationsProvider} from './mediaOperationsProvider';
import {useTranscriptDataProvider} from './transcriptProvider';
import {useMediaTabsProvider} from './mediaTabsProvider';
import {useTeamsStore} from './teamsStore';
import {useShallow} from 'zustand/react/shallow';

interface IStateProviderProps {
   entryPoint?: PWCEntryPoint;
   isOwnerOrAdmin?: boolean;
   children?: React.ReactElement;
}

export enum PWCEntryPoint {
   Header = 'header',
   WatchPage = 'watchPage',
   Library = 'library',
   Teams = 'teams',
   Collections = 'collections',
   Embed = 'embed',
   MigrationOrMediaDownload = 'migrationOrMediaDownload',
   Default = 'default'
}

type IProviderFunction = (children: ReactElement) => React.JSX.Element;

const providersMapping: Record<PWCEntryPoint, IProviderFunction[]> = {
   [PWCEntryPoint.Header]: [
      useLibraryProvider,
      useCollectionsProvider,
      useFoldersProvider
   ],
   [PWCEntryPoint.Library]: [
      useLibraryProvider,
      useCollectionsProvider,
      useCollectionMediaProvider,
      useFoldersProvider,
      useBatchSelectionProvider,
      useMediaProvider,
      useSortMetricProvider,
      useMigrationProvider
   ],
   [PWCEntryPoint.Teams]: [
      useLibraryProvider,
      useCollectionsProvider,
      useFoldersProvider,
      useMediaProvider,
      useBatchSelectionProvider,
      useSortMetricProvider
   ],
   [PWCEntryPoint.Collections]: [
      useLibraryProvider,
      useCollectionsProvider,
      useCollectionMediaProvider,
      useFoldersProvider,
      useBatchSelectionProvider,
      useMediaProvider,
      useSortMetricProvider,
      useMigrationProvider
   ],
   [PWCEntryPoint.WatchPage]: [
      useWindowSizingProvider,
      useSelectedConversationProvider,
      useSelectedReactionGroupProvider,
      useCanvasProvider,
      useCommentsProvider,
      useAnnotationManagerProvider,
      useConversationsProvider,
      useReactionsProvider,
      useDisableConversationProvider,
      useCollectionsProvider,
      useMediaProvider,
      useCaptionDataProvider,
      useFoldersProvider,
      useSelfIdentifiedUserProvider,
      useMigrationProvider,
      useMediaOperationsProvider,
      useTranscriptDataProvider,
      useMediaTabsProvider
   ],
   [PWCEntryPoint.Embed]: [
      useMediaProvider,
      useConversationsProvider,
      useReactionsProvider,
      useSelectedReactionGroupProvider,
      useSelfIdentifiedUserProvider
   ],
   [PWCEntryPoint.MigrationOrMediaDownload]: [
      useMigrationProvider
   ],
   [PWCEntryPoint.Default]: [
      useLibraryProvider,
      useCollectionsProvider,
      useFoldersProvider
   ]
};

const StateProvider: React.FC<IStateProviderProps> = ({entryPoint = PWCEntryPoint.Default, children}) => {
   const refreshUserQuotasFromApi = useUserQuotaStore(s => s.refreshUserQuotasFromApi);
   const {updateUserTeamsWithFolderTrees, updateTeamInvitationList} = useTeamsStore(useShallow(s => ({
      updateUserTeamsWithFolderTrees: s.updateUserTeamsWithFolderTrees,
      updateTeamInvitationList: s.updateTeamInvitationList
   })));

   const entryPointInitializationFunctions: Record<PWCEntryPoint, (() => Promise<any>)[]> = useMemo(() => ({
      [PWCEntryPoint.Header]: [refreshUserQuotasFromApi, updateUserTeamsWithFolderTrees, updateTeamInvitationList],
      [PWCEntryPoint.Library]: [refreshUserQuotasFromApi, updateUserTeamsWithFolderTrees, updateTeamInvitationList],
      [PWCEntryPoint.Teams]: [refreshUserQuotasFromApi, updateUserTeamsWithFolderTrees, updateTeamInvitationList],
      [PWCEntryPoint.Collections]: [refreshUserQuotasFromApi, updateUserTeamsWithFolderTrees, updateTeamInvitationList],
      [PWCEntryPoint.WatchPage]: [refreshUserQuotasFromApi],
      [PWCEntryPoint.Embed]: [refreshUserQuotasFromApi],
      [PWCEntryPoint.MigrationOrMediaDownload]: [() => Promise.resolve()],
      [PWCEntryPoint.Default]: [refreshUserQuotasFromApi, updateUserTeamsWithFolderTrees, updateTeamInvitationList]
   }), [refreshUserQuotasFromApi]);

   const functionsToInitialize = useMemo(() => entryPointInitializationFunctions[entryPoint], [entryPoint, entryPointInitializationFunctions]);

   useEffect(() => {
      void Promise.all(functionsToInitialize.map(f => f()));
   }, [functionsToInitialize]);

   const providers = providersMapping[entryPoint];
   return providers.reduce((tree, provider) => provider(tree), children);
};

export default StateProvider;